import { Component, ChangeDetectionStrategy, input, output, signal, computed } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { User, UserPlan } from '../../models/user.model';

export type SortableUserColumns = keyof User | 'createdAt' | '';

@Component({
  selector: 'app-user-management',
  standalone: true,
  template: `
<div class="bg-white p-6 md:p-8 rounded-2xl shadow-card border border-slate-200/80">
  <div class="flex flex-col sm:flex-row justify-between items-start sm:items-center mb-6 gap-4">
    <div>
       <h2 class="text-2xl font-bold text-slate-900">Gerenciamento de Usuários</h2>
       <p class="text-sm text-gray-500">Encontre e gerencie todos os usuários da plataforma.</p>
    </div>
    <button (click)="openCreateModal()" class="flex-shrink-0 flex items-center bg-custom-blue text-white font-semibold py-2 px-4 rounded-lg hover:bg-indigo-700 transition-opacity">
      <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor" class="w-5 h-5 mr-2">
        <path d="M10.75 4.75a.75.75 0 0 0-1.5 0v4.5h-4.5a.75.75 0 0 0 0 1.5h4.5v4.5a.75.75 0 0 0 1.5 0v-4.5h4.5a.75.75 0 0 0 0-1.5h-4.5v-4.5Z" />
      </svg>
      Criar Usuário
    </button>
  </div>

  <div class="mb-4">
    <label for="user-search" class="sr-only">Buscar usuário</label>
    <div class="relative">
      <div class="pointer-events-none absolute inset-y-0 left-0 flex items-center pl-3">
         <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor" class="w-5 h-5 text-gray-400"><path fill-rule="evenodd" d="M9 3.5a5.5 5.5 0 1 0 0 11 5.5 5.5 0 0 0 0-11ZM2 9a7 7 0 1 1 12.452 4.391l3.328 3.329a.75.75 0 1 1-1.06 1.06l-3.329-3.328A7 7 0 0 1 2 9Z" clip-rule="evenodd" /></svg>
      </div>
      <input type="text" id="user-search" [value]="filterTerm()" (input)="onFilter($event)" class="block w-full rounded-lg border border-gray-300 dark:border-gray-600 pl-10 py-2 text-slate-800 dark:text-gray-200 dark:bg-gray-700 dark:border-gray-600 placeholder-gray-500 focus:border-custom-blue focus:ring-custom-blue sm:text-sm" placeholder="Buscar por nome ou email..."/>
    </div>
  </div>
  
  <div class="overflow-x-auto">
    <table class="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
      <thead class="bg-custom-gray dark:bg-gray-700">
        <tr>
          <th scope="col" (click)="sortBy('name')" class="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider cursor-pointer">Nome</th>
          <th scope="col" (click)="sortBy('plan')" class="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider cursor-pointer">Plano</th>
          <th scope="col" (click)="sortBy('credits')" class="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider cursor-pointer">Créditos</th>
          <th scope="col" (click)="sortBy('createdAt')" class="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider cursor-pointer">Membro Desde</th>
          <th scope="col" class="relative px-6 py-3"><span class="sr-only">Ações</span></th>
        </tr>
      </thead>
      <tbody class="bg-white divide-y divide-gray-200 dark:divide-gray-700">
        @for (user of paginatedUsers(); track user.id) {
          <tr class="hover:bg-custom-gray dark:hover:bg-gray-700/50">
            <td class="px-6 py-4 whitespace-nowrap"><div class="text-sm font-medium">{{ user.name }}</div><div class="text-sm text-gray-500">{{ user.email }}</div></td>
            <td class="px-6 py-4 whitespace-nowrap"><span class="px-2 inline-flex text-xs leading-5 font-semibold rounded-full" [class]="getPlanClasses(user.plan)">{{ getPlanLabel(user.plan) }}</span></td>
            <td class="px-6 py-4 whitespace-nowrap text-sm font-medium">{{ user.credits }}</td>
            <td class="px-6 py-4 whitespace-nowrap text-sm text-gray-500">{{ user.createdAt | date: 'dd/MM/yyyy' }}</td>
            <td class="px-6 py-4 whitespace-nowrap text-right text-sm font-medium space-x-2">
              <button (click)="onEdit(user)" class="text-custom-blue hover:text-blue-700 font-semibold">Editar</button>
              <button (click)="onDelete(user.id)" class="text-red-600 hover:text-red-800 font-semibold">Excluir</button>
            </td>
          </tr>
        } @empty {
          <tr><td colspan="5" class="px-6 py-4 text-center text-gray-500">Nenhum usuário encontrado.</td></tr>
        }
      </tbody>
    </table>
  </div>
   <div class="flex items-center justify-between mt-4 text-sm">
    <span class="text-gray-600 dark:text-gray-400">Página {{ currentPage() }} de {{ totalPages() }}</span>
    <div class="flex items-center space-x-2">
      <button (click)="changePage(currentPage() - 1)" [disabled]="currentPage() === 1" class="px-3 py-1.5 border border-gray-300 dark:border-gray-600 rounded-lg text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700 dark:hover:bg-gray-700 disabled:opacity-50 disabled:cursor-not-allowed">Anterior</button>
      <button (click)="changePage(currentPage() + 1)" [disabled]="currentPage() === totalPages()" class="px-3 py-1.5 border border-gray-300 dark:border-gray-600 rounded-lg text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700 dark:hover:bg-gray-700 disabled:opacity-50 disabled:cursor-not-allowed">Próximo</button>
    </div>
  </div>
</div>

@if (isCreateModalOpen()) {
  <div class="fixed inset-0 bg-black bg-opacity-60 z-40 flex items-center justify-center" (click)="closeCreateModal()">
    <div class="bg-white rounded-2xl shadow-card border border-slate-200/80 p-8 w-full max-w-lg" (click)="$event.stopPropagation()">
      <h3 class="text-xl font-bold mb-4">Criar Novo Usuário</h3>
      <form #newUserForm="ngForm" (ngSubmit)="saveNewUser()" class="space-y-4">
        <div><label for="newUserName" class="block text-sm font-medium">Nome</label><input type="text" id="newUserName" name="newUserName" [(ngModel)]="newUserName" required class="mt-1 block w-full appearance-none rounded-md border border-gray-300 dark:border-gray-600 dark:bg-gray-700 px-3 py-2 sm:text-sm"></div>
        <div><label for="newUserEmail" class="block text-sm font-medium">Email</label><input type="email" id="newUserEmail" name="newUserEmail" [(ngModel)]="newUserEmail" required class="mt-1 block w-full appearance-none rounded-md border border-gray-300 dark:border-gray-600 dark:bg-gray-700 px-3 py-2 sm:text-sm"></div>
        <div class="grid grid-cols-2 gap-4">
           <div><label for="newUserPlan" class="block text-sm font-medium">Plano</label><select id="newUserPlan" name="newUserPlan" [(ngModel)]="newUserPlan" class="mt-1 block w-full rounded-md border-gray-300 dark:border-gray-600 dark:bg-gray-700 px-3 py-2 sm:text-sm">@for(p of availablePlans; track p){<option [value]="p">{{p}}</option>}</select></div>
           <div><label for="newUserCredits" class="block text-sm font-medium">Créditos</label><input type="number" id="newUserCredits" name="newUserCredits" [(ngModel)]="newUserCredits" required class="mt-1 block w-full appearance-none rounded-md border border-gray-300 dark:border-gray-600 dark:bg-gray-700 px-3 py-2 sm:text-sm"></div>
        </div>
        <div class="flex justify-end space-x-3 pt-4">
          <button type="button" (click)="closeCreateModal()" class="px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-md text-sm font-medium hover:bg-gray-50 dark:hover:bg-gray-700 dark:hover:bg-gray-700">Cancelar</button>
          <button type="submit" [disabled]="!newUserForm.form.valid" class="px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-custom-blue hover:bg-indigo-700 disabled:bg-gray-400">Criar Usuário</button>
        </div>
      </form>
    </div>
  </div>
}
`,
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [CommonModule, FormsModule]
})
export class UserManagementComponent {
  users = input.required<User[]>();
  selectUser = output<User>();
  userCreate = output<Pick<User, 'name' | 'email' | 'credits' | 'plan'>>();
  userDelete = output<string>();

  readonly filterTerm = signal('');
  readonly sortColumn = signal<SortableUserColumns>('createdAt');
  readonly sortDirection = signal<'asc' | 'desc'>('desc');
  readonly currentPage = signal(1);
  readonly pageSize = signal(10);
  readonly isCreateModalOpen = signal(false);
  readonly newUserName = signal('');
  readonly newUserEmail = signal('');
  readonly newUserPlan = signal<UserPlan>('gratuito');
  readonly newUserCredits = signal(1);
  readonly availablePlans: UserPlan[] = ['gratuito', 'basico', 'intermediario', 'pro'];

  readonly processedUsers = computed(() => {
    const term = this.filterTerm().toLowerCase();
    const column = this.sortColumn();
    const direction = this.sortDirection();

    const users = this.users().filter(user =>
      user.name.toLowerCase().includes(term) ||
      user.email.toLowerCase().includes(term)
    );

    if (column) {
      users.sort((a, b) => {
        const aValue = a[column as keyof User];
        const bValue = b[column as keyof User];

        if (aValue === undefined || aValue === null) return 1;
        if (bValue === undefined || bValue === null) return -1;

        let comparison = 0;
        if (typeof aValue === 'string' && typeof bValue === 'string') {
          comparison = aValue.localeCompare(bValue);
        } else if (aValue instanceof Date && bValue instanceof Date) {
          comparison = aValue.getTime() - bValue.getTime();
        } else {
          comparison = Number(aValue) - Number(bValue);
        }

        return direction === 'asc' ? comparison : -comparison;
      });
    }

    return users;
  });

  readonly totalPages = computed(() => Math.ceil(this.processedUsers().length / this.pageSize()));

  readonly paginatedUsers = computed(() => {
    const startIndex = (this.currentPage() - 1) * this.pageSize();
    return this.processedUsers().slice(startIndex, startIndex + this.pageSize());
  });

  onFilter(event: Event) {
    this.currentPage.set(1);
    const input = event.target as HTMLInputElement;
    this.filterTerm.set(input.value);
  }

  sortBy(column: SortableUserColumns) {
    if (this.sortColumn() === column) {
      this.sortDirection.update(d => d === 'asc' ? 'desc' : 'asc');
    } else {
      this.sortColumn.set(column);
      this.sortDirection.set('asc');
    }
    this.currentPage.set(1);
  }

  changePage(page: number) {
    if (page > 0 && page <= this.totalPages()) {
      this.currentPage.set(page);
    }
  }

  openCreateModal() {
    this.newUserName.set('');
    this.newUserEmail.set('');
    this.newUserCredits.set(1);
    this.newUserPlan.set('gratuito');
    this.isCreateModalOpen.set(true);
  }

  closeCreateModal() {
    this.isCreateModalOpen.set(false);
  }

  saveNewUser() {
    this.userCreate.emit({
      name: this.newUserName(),
      email: this.newUserEmail(),
      credits: this.newUserCredits(),
      plan: this.newUserPlan()
    });
    this.closeCreateModal();
  }

  onEdit(user: User) {
    this.selectUser.emit(user);
  }

  onDelete(userId: string) {
    this.userDelete.emit(userId);
  }

  getPlanClasses(plan: UserPlan | undefined): string {
    switch (plan) {
      case 'pro': return 'bg-blue-100 text-blue-800 dark:bg-blue-900/50 dark:text-blue-300';
      case 'intermediario': return 'bg-purple-100 text-purple-800 dark:bg-purple-900/50 dark:text-purple-300';
      case 'basico': return 'bg-green-100 text-green-800 dark:bg-green-900/50 dark:text-green-300';
      case 'gratuito':
      default: return 'bg-gray-100 text-gray-800 dark:bg-gray-700 dark:text-gray-300';
    }
  }

  getPlanLabel(plan: UserPlan | undefined): string {
    if (!plan) return 'N/A';
    return plan.charAt(0).toUpperCase() + plan.slice(1);
  }
}
